/**
 * DedupPreviewTable — step 3 of the import wizard.
 *
 * Renders one card per preview record with an explicit "create / merge /
 * skip" choice. High-confidence dedup matches (server-suggested 'merge')
 * are flagged "Decision required" and surface a side-by-side comparison
 * of the incoming row vs. the matched existing record. The parent
 * disables the Continue button until every required decision has been
 * actively confirmed by the operator — no silent merges.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PreviewRecord } from '../types/importBatch';

type Action = 'create' | 'merge' | 'skip';

interface DedupPreviewTableProps {
  records: PreviewRecord[];
  actions: Record<number, Action>;
  /** Row indices the operator has actively confirmed (touched the picker). */
  confirmed: Set<number>;
  onChange: (row: number, action: Action) => void;
}


/** A row needs explicit confirmation if the server proposed a merge
 *  (high-confidence match) or flagged it for review. We never silently
 *  apply either — Brian wants a real human decision on every dupe. */
const requiresDecision = (rec: PreviewRecord): boolean =>
  rec.suggested_action === 'merge' || rec.suggested_action === 'review';


const actionLabel = (a: Action): string =>
  a === 'create' ? 'Create new' : a === 'merge' ? 'Merge with existing' : 'Skip';


const KV: React.FC<{ label: string; value?: any }> = ({ label, value }) => (
  <div className="flex gap-2 text-xs">
    <span className="text-gray-500 w-16 shrink-0">{label}</span>
    <span className="text-gray-800 truncate">
      {value === undefined || value === null || value === '' ? '—' : String(value)}
    </span>
  </div>
);


const DedupPreviewTable: React.FC<DedupPreviewTableProps> = ({
  records, actions, confirmed, onChange,
}) => {
  return (
    <div className="space-y-3">
      {records.map((rec) => {
        const top = rec.dedup_matches[0];
        const fallback = rec.suggested_action;
        const currentAction: Action =
          (actions[rec.row] as Action) ??
          (fallback === 'review' || fallback === 'merge'
            ? 'merge'   // pre-fills the picker, but row stays unconfirmed
            : (fallback as Action));

        const needsDecision = requiresDecision(rec) && !confirmed.has(rec.row);
        const containerClass = rec.errors.length
          ? 'border-red-300 bg-red-50'
          : needsDecision
          ? 'border-amber-300 bg-amber-50'
          : 'border-gray-200 bg-white';

        return (
          <div
            key={rec.row}
            className={`border rounded-lg p-3 ${containerClass}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 font-mono">
                    Row {rec.row + 1}
                  </span>
                  {needsDecision ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-200 text-amber-900">
                      <AlertTriangle className="w-3 h-3" />
                      Decision required
                    </span>
                  ) : confirmed.has(rec.row) ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">
                      <CheckCircle2 className="w-3 h-3" />
                      {actionLabel(currentAction)}
                    </span>
                  ) : null}
                </div>

                {needsDecision ? (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="border border-gray-200 bg-white rounded p-2">
                      <div className="text-xs font-semibold text-gray-700 mb-1">
                        Incoming row
                      </div>
                      <KV label="Name" value={rec.record.full_name || rec.record.name} />
                      <KV label="Firm" value={rec.record.firm_name} />
                      <KV label="Email" value={rec.record.email || rec.record.contact_email} />
                      <KV label="Website" value={rec.record.website || rec.record.website_url} />
                    </div>
                    <div className="border border-gray-200 bg-white rounded p-2">
                      <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center justify-between">
                        <span>Existing match</span>
                        {top ? (
                          <span className="text-xs font-normal text-gray-500">
                            {top.confidence}% match
                          </span>
                        ) : null}
                      </div>
                      <KV label="Name" value={top?.existing_name} />
                      <KV label="ID" value={top?.existing_id ? `#${top.existing_id}` : null} />
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Open the existing record in a new tab if you need
                        to compare more fields before deciding.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-800">
                    <div className="font-medium truncate">
                      {rec.record.full_name ||
                        rec.record.name ||
                        <span className="text-red-600">(missing name)</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {[rec.record.firm_name, rec.record.email, rec.record.website]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                )}

                {rec.errors.length > 0 && (
                  <div className="text-xs text-red-700 mt-2">
                    {rec.errors.join('; ')}
                  </div>
                )}
              </div>

              <div className="shrink-0">
                <select
                  value={currentAction}
                  onChange={(e) => onChange(rec.row, e.target.value as Action)}
                  className={`px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    needsDecision
                      ? 'border-amber-400 bg-white font-medium'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <option value="create">Create new</option>
                  <option value="merge" disabled={!top}>
                    Merge with existing{top ? ` (${top.confidence}%)` : ''}
                  </option>
                  <option value="skip">Skip</option>
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DedupPreviewTable;
